/**
 * PATH: apps/qa/src/lib/floorBuilderAI.ts
 * PURPOSE: AI analysis logic for intelligent floor builder suggestions
 * 
 * WHY: Automate floor structure suggestions based on:
 *   - Organisation's primary trade (e.g., Bravada = Waterproofing → Wet Areas)
 *   - Building class (Class 2 = Residential → typical apartment layouts)
 *   - Project type (New construction vs Remediation)
 *   - Job context (name patterns like "NEL", "SPARK")
 * 
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌─────────────┐
 *   │ Job + Trade │───▶│ Analyze      │───▶│ Suggestions │
 *   │ Context     │    │ Patterns     │    │ + Confidence│
 *   └─────────────┘    └──────────────┘    └─────────────┘
 */

import type { AISuggestions, AIAnalysisInput } from '@/components/inspection/FloorBuilder/types'

// ─────────────────────────────────────────────────────────────────────────────
// Trade → Area Mappings
// ─────────────────────────────────────────────────────────────────────────────

const TRADE_AREA_MAP: Record<string, string[]> = {
  // Core waterproofing-related trades
  'Waterproofing': ['Wet Areas', 'Balconies', 'Planter Boxes', 'Risers'],
  'Membrane': ['Wet Areas', 'Balconies', 'Podium', 'Risers'],
  'Tanking': ['Basement', 'Retaining Walls', 'Lift Pits'],
  
  // Fire & safety
  'Fire Protection': ['Risers', 'Plant Rooms', 'Stairwells', 'Corridors', 'Fire Exits'],
  'Fire Stopping': ['Risers', 'Service Penetrations', 'Stairwells'],
  'Passive Fire': ['Risers', 'Ceiling Spaces', 'Walls'],
  
  // Structural & envelope
  'Structural': ['All Areas', 'Columns', 'Beams', 'Slabs'],
  'Façade': ['External Walls', 'Windows', 'Balconies', 'Cladding'],
  'Roofing': ['Rooftop', 'Membrane', 'Flashing'],
  'Cladding': ['External Walls', 'Façade Panels'],
  
  // Services
  'HVAC': ['Plant Rooms', 'Risers', 'Ceiling Spaces', 'Ductwork'],
  'Electrical': ['Risers', 'Switch Rooms', 'Distribution Boards'],
  'Plumbing': ['Wet Areas', 'Risers', 'Plant Rooms'],
  'Hydraulics': ['Wet Areas', 'Risers', 'Plant Rooms', 'Fire Services'],
  
  // Finishes
  'Tiling': ['Wet Areas', 'Balconies', 'Common Areas'],
  'Painting': ['All Areas', 'Common Areas', 'Corridors'],
  'Flooring': ['All Areas', 'Corridors', 'Lobbies'],
  'Joinery': ['Kitchens', 'Bathrooms', 'Storage'],
}

// ─────────────────────────────────────────────────────────────────────────────
// Building Class → Floor Count Estimates
// ─────────────────────────────────────────────────────────────────────────────

const CLASS_FLOOR_ESTIMATES: Record<string, { min: number; typical: number; max: number }> = {
  'Class 1a': { min: 1, typical: 2, max: 3 },      // Single dwelling
  'Class 1b': { min: 1, typical: 2, max: 4 },      // Boarding house
  'Class 2': { min: 3, typical: 8, max: 50 },      // Apartments
  'Class 3': { min: 2, typical: 5, max: 20 },      // Hotels/hostels
  'Class 4': { min: 1, typical: 1, max: 2 },       // Dwelling in Class 5-9
  'Class 5': { min: 2, typical: 10, max: 50 },     // Office
  'Class 6': { min: 1, typical: 2, max: 5 },       // Retail
  'Class 7a': { min: 1, typical: 1, max: 3 },      // Carpark
  'Class 7b': { min: 1, typical: 2, max: 5 },      // Warehouse
  'Class 8': { min: 1, typical: 2, max: 5 },       // Factory
  'Class 9a': { min: 1, typical: 3, max: 10 },     // Healthcare
  'Class 9b': { min: 1, typical: 4, max: 10 },     // Assembly (schools, etc)
  'Class 9c': { min: 1, typical: 2, max: 5 },      // Aged care
  'Class 10a': { min: 1, typical: 1, max: 2 },     // Non-habitable
  'Class 10b': { min: 1, typical: 1, max: 1 },     // Structures
  'Class 10c': { min: 1, typical: 1, max: 1 },     // Private bushfire shelters
}

// ─────────────────────────────────────────────────────────────────────────────
// Project Type → Area Patterns
// ─────────────────────────────────────────────────────────────────────────────

const PROJECT_TYPE_AREAS: Record<string, string[]> = {
  'new_construction': ['All typical areas'],
  'renovation': ['Affected Areas', 'Interface Zones', 'Protection Zones'],
  'remediation': ['Defect Areas', 'Repair Zones', 'Investigation Points'],
}

// ─────────────────────────────────────────────────────────────────────────────
// Category → Default Areas
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_DEFAULT_AREAS: Record<string, { typical: string[]; basement: string[]; ground: string[]; rooftop: string[] }> = {
  'residential': {
    typical: ['Wet Areas', 'Balconies', 'Living Areas', 'Bedrooms'],
    basement: ['Carpark', 'Storage', 'Plant Room'],
    ground: ['Lobby', 'Entry', 'Mail Room', 'Bin Room'],
    rooftop: ['Plant', 'Access Paths', 'Membrane'],
  },
  'commercial': {
    typical: ['Office Space', 'Wet Areas', 'Corridors', 'Risers'],
    basement: ['Carpark', 'Loading Dock', 'Plant Room', 'Storage'],
    ground: ['Lobby', 'Reception', 'Fire Stairs', 'Lifts'],
    rooftop: ['Plant Room', 'HVAC', 'Access Paths'],
  },
  'infrastructure': {
    typical: ['General Areas', 'Service Zones', 'Corridors'],
    basement: ['Services', 'Plant', 'Storage'],
    ground: ['Entry', 'Reception', 'Common Areas'],
    rooftop: ['Plant', 'Services', 'Access'],
  },
}

// ─────────────────────────────────────────────────────────────────────────────
// Name Pattern Detection
// ─────────────────────────────────────────────────────────────────────────────

interface PatternMatch {
  pattern: RegExp
  floorEstimate?: number
  includeBasement?: boolean
  includeRooftop?: boolean
  additionalAreas?: string[]
}

const NAME_PATTERNS: PatternMatch[] = [
  // High-rise indicators
  { pattern: /tower|high.?rise/i, floorEstimate: 20, includeBasement: true, includeRooftop: true },
  
  // School/education
  { pattern: /school|college|university|education/i, floorEstimate: 3, includeRooftop: false },
  
  // Hospital/healthcare
  { pattern: /hospital|medical|health|clinic/i, floorEstimate: 5, includeBasement: true },
  
  // Infrastructure projects (SPARK, NEL, DPIE)
  { pattern: /spark|nel|dpie|nsw.?health/i, floorEstimate: 4, includeBasement: true, includeRooftop: true },
  
  // Underslab work (typically basement/ground focus)
  { pattern: /underslab|under.?slab/i, floorEstimate: 2, includeBasement: true, additionalAreas: ['Underslab', 'Slab Edge'] },
  
  // Podium
  { pattern: /podium/i, floorEstimate: 3, additionalAreas: ['Podium', 'Podium Deck'] },
  
  // Carpark
  { pattern: /car.?park|parking/i, floorEstimate: 3, includeBasement: true, additionalAreas: ['Carpark', 'Ramps'] },
]

// ─────────────────────────────────────────────────────────────────────────────
// Main Analysis Function
// ─────────────────────────────────────────────────────────────────────────────

export function analyzeForFloorBuilder(input: AIAnalysisInput): AISuggestions {
  const {
    jobId,
    jobName = '',
    clientName = '',
    buildingClasses = [],
    projectType = 'new_construction',
    category = 'commercial',
    tradeTags = [],
    orgPrimaryTrade = '',
  } = input

  // Initialize with defaults
  let floorCount = 1
  let includeBasement = false
  let includeRooftop = false
  let confidence = 0.3  // Base confidence
  const reasoningParts: string[] = []
  
  // Collect areas from all sources
  const typicalAreasSet = new Set<string>()
  const basementAreasSet = new Set<string>()
  const groundAreasSet = new Set<string>()
  const rooftopAreasSet = new Set<string>()
  const sourceTrades: string[] = []

  // ─────────────────────────────────────────────────────────────────────────
  // 1. Organization's Primary Trade (Highest Priority)
  // ─────────────────────────────────────────────────────────────────────────
  
  if (orgPrimaryTrade) {
    const primaryTradeAreas = TRADE_AREA_MAP[orgPrimaryTrade]
    if (primaryTradeAreas) {
      primaryTradeAreas.forEach(area => typicalAreasSet.add(area))
      sourceTrades.push(orgPrimaryTrade)
      confidence += 0.2
      reasoningParts.push(`Primary trade: ${orgPrimaryTrade}`)
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 2. Selected Trade Tags
  // ─────────────────────────────────────────────────────────────────────────
  
  tradeTags.forEach(trade => {
    const tradeAreas = TRADE_AREA_MAP[trade]
    if (tradeAreas) {
      tradeAreas.forEach(area => typicalAreasSet.add(area))
      if (!sourceTrades.includes(trade)) sourceTrades.push(trade)
    }
  })
  
  if (tradeTags.length > 0) {
    confidence += Math.min(0.15, tradeTags.length * 0.03)
    reasoningParts.push(`${tradeTags.length} trade(s) selected`)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 3. Building Class Analysis
  // ─────────────────────────────────────────────────────────────────────────
  
  if (buildingClasses.length > 0) {
    const primaryClass = buildingClasses[0]
    const classEstimate = CLASS_FLOOR_ESTIMATES[primaryClass]
    
    if (classEstimate) {
      floorCount = classEstimate.typical
      confidence += 0.15
      reasoningParts.push(`${primaryClass} building`)
      
      // High-rise classes typically have basement/rooftop
      if (classEstimate.typical >= 5) {
        includeBasement = true
        includeRooftop = true
      }
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 4. Job Name Pattern Matching
  // ─────────────────────────────────────────────────────────────────────────
  
  const combinedName = `${jobName} ${clientName}`.toLowerCase()
  
  for (const pattern of NAME_PATTERNS) {
    if (pattern.pattern.test(combinedName)) {
      if (pattern.floorEstimate) {
        floorCount = Math.max(floorCount, pattern.floorEstimate)
      }
      if (pattern.includeBasement !== undefined) {
        includeBasement = pattern.includeBasement
      }
      if (pattern.includeRooftop !== undefined) {
        includeRooftop = pattern.includeRooftop
      }
      if (pattern.additionalAreas) {
        pattern.additionalAreas.forEach(area => typicalAreasSet.add(area))
      }
      confidence += 0.1
      
      // Extract the matched keyword for reasoning
      const match = combinedName.match(pattern.pattern)
      if (match) {
        reasoningParts.push(`"${match[0]}" detected in job name`)
      }
      break  // Only match first pattern
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 5. Category Defaults (if no trade-specific areas found)
  // ─────────────────────────────────────────────────────────────────────────
  
  const categoryDefaults = CATEGORY_DEFAULT_AREAS[category] || CATEGORY_DEFAULT_AREAS['commercial']
  
  // Add category defaults for special floors
  categoryDefaults.basement.forEach(area => basementAreasSet.add(area))
  categoryDefaults.ground.forEach(area => groundAreasSet.add(area))
  categoryDefaults.rooftop.forEach(area => rooftopAreasSet.add(area))
  
  // If no trade-specific areas, use category typical areas
  if (typicalAreasSet.size === 0) {
    categoryDefaults.typical.forEach(area => typicalAreasSet.add(area))
    reasoningParts.push(`${category} category defaults`)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 6. Ensure Minimum Areas
  // ─────────────────────────────────────────────────────────────────────────
  
  // Always include at least one area
  if (typicalAreasSet.size === 0) {
    typicalAreasSet.add('General Area')
  }

  // ─────────────────────────────────────────────────────────────────────────
  // 7. Cap Confidence
  // ─────────────────────────────────────────────────────────────────────────
  
  confidence = Math.min(0.95, confidence)

  // ─────────────────────────────────────────────────────────────────────────
  // 8. Generate Reasoning String
  // ─────────────────────────────────────────────────────────────────────────
  
  const reasoning = reasoningParts.length > 0 
    ? `Based on: ${reasoningParts.join(', ')}`
    : 'Using default structure'

  return {
    floorCount,
    includeBasement,
    includeRooftop,
    typicalAreas: Array.from(typicalAreasSet),
    basementAreas: Array.from(basementAreasSet),
    groundAreas: Array.from(groundAreasSet),
    rooftopAreas: Array.from(rooftopAreasSet),
    confidence,
    reasoning,
    sourceTrades,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Available Areas for Selection
// ─────────────────────────────────────────────────────────────────────────────

export const AVAILABLE_TYPICAL_AREAS = [
  // Waterproofing-related
  'Wet Areas',
  'Balconies',
  'Planter Boxes',
  'Risers',
  'Podium',
  
  // Structural
  'Columns',
  'Beams',
  'Slabs',
  'Walls',
  
  // Fire & Safety
  'Fire Stairs',
  'Fire Exits',
  'Corridors',
  'Service Penetrations',
  
  // Services
  'Plant Rooms',
  'Ceiling Spaces',
  'Ductwork',
  'Switch Rooms',
  
  // Residential
  'Living Areas',
  'Bedrooms',
  'Kitchens',
  'Storage',
  
  // Commercial
  'Office Space',
  'Meeting Rooms',
  'Reception',
  'Lobby',
  
  // External
  'External Walls',
  'Windows',
  'Cladding',
  'Façade',
  
  // General
  'General Area',
  'Common Areas',
  'All Areas',
]

export const AVAILABLE_BASEMENT_AREAS = [
  'Carpark',
  'Storage',
  'Plant Room',
  'Loading Dock',
  'Services',
  'Lift Pits',
  'Pump Room',
  'Fire Tank',
  'Retaining Walls',
]

export const AVAILABLE_ROOFTOP_AREAS = [
  'Plant',
  'HVAC',
  'Access Paths',
  'Membrane',
  'Flashing',
  'Penetrations',
  'Skylights',
  'Roof Drainage',
]

// ─────────────────────────────────────────────────────────────────────────────
// Estimate Inspection Time
// ─────────────────────────────────────────────────────────────────────────────

export function estimateInspectionTime(
  floorCount: number,
  typicalAreasPerFloor: number,
  includeBasement: boolean,
  includeRooftop: boolean
): { min: number; max: number; formatted: string } {
  // Base time per floor (minutes)
  const baseTimePerFloor = 15
  const timePerArea = 5
  
  const totalFloors = floorCount + (includeBasement ? 1 : 0) + (includeRooftop ? 1 : 0)
  const totalAreas = totalFloors * typicalAreasPerFloor
  
  const minMinutes = totalFloors * baseTimePerFloor + totalAreas * (timePerArea * 0.5)
  const maxMinutes = totalFloors * baseTimePerFloor + totalAreas * (timePerArea * 1.5)
  
  const minHours = Math.ceil(minMinutes / 60)
  const maxHours = Math.ceil(maxMinutes / 60)
  
  let formatted: string
  if (minHours === maxHours) {
    formatted = `~${minHours} hour${minHours !== 1 ? 's' : ''}`
  } else {
    formatted = `${minHours}-${maxHours} hours`
  }
  
  return { min: minMinutes, max: maxMinutes, formatted }
}
